import { addDays, formatISO } from "date-fns";
import { ListFilterOperator, SortDirection } from "@common/api/filterTypes";
import { RelativeDateDirection, RelativeDateUnit } from "../annualWheel/RelativeDate.types";
import { DateFieldFilterEnum } from "@common/components/filterPopover/subMenues/DateFieldSubMenu";
import { boolParam } from "@common/hooks/useSeedFilterFromUrl";
import { ContractFilterRequest, DateRangeFilter } from "./ContractFilter.types";
import { ContractFilterStatus } from "./Contracts.types";
import { ContractColumn } from "./contractDisplayFields/ContractDisplayFields.types";

// A link from elsewhere, such as a key figure on the contract dashboard, that opens the contract list on the contracts
// it counted. The list applies it once on arrival and strips it from the url.
export interface ContractListDeepLink {
    status?: ContractFilterStatus;
    // The statuses a contract must not have. The list's Approved and Draft also hold contracts past their end date, which
    // the dashboard counts as Expired only, so leaving out the other statuses is how a link lands on just the running ones.
    excludedStatuses?: Array<ContractFilterStatus>;
    endDateWithinYears?: number;
    endDateWithinDays?: number;
    endDatePassed?: boolean;
    withoutEndDate?: boolean;
    withinExpiryNotificationPeriod?: boolean;
    autoRenewal?: boolean;
    hasBusinessArea?: boolean;
    hasExternalContractParty?: boolean;
    hasFinalizedAmount?: boolean;
    sortByFinalizedAmount?: boolean;
    showOnlyMine?: boolean;
    groupEntityId?: string;
    currencyCode?: string;
    endDateYear?: number;
    endDateYearOrLater?: boolean;
    templateIds?: Array<string>;
}

const deepLinkParams = {
    status: "status",
    excludedStatuses: "excludedStatus",
    endDateWithinYears: "endDateWithinYears",
    endDateWithinDays: "endDateWithinDays",
    endDatePassed: "endDatePassed",
    withoutEndDate: "withoutEndDate",
    withinExpiryNotificationPeriod: "withinExpiryNotificationPeriod",
    autoRenewal: "autoRenewal",
    hasBusinessArea: "hasBusinessArea",
    hasExternalContractParty: "hasExternalContractParty",
    hasFinalizedAmount: "hasFinalizedAmount",
    sortByFinalizedAmount: "sortByFinalizedAmount",
    showOnlyMine: "showOnlyMine",
    groupEntityId: "groupEntityId",
    currencyCode: "currencyCode",
    endDateYear: "endDateYear",
    endDateYearOrLater: "endDateYearOrLater",
    templateIds: "templateId",
} as const satisfies Record<keyof ContractListDeepLink, string>;

export const contractListDeepLinkParams: Array<string> = Object.values(deepLinkParams);

// The list only sorts by a column it shows, and a list sorted by value should show the value it is sorted by.
const contractValueColumnIds = ["finalized-amount-range", "currency-codes"];

export function withDeepLinkColumns(params: URLSearchParams, selectedColumnIds: Array<string>) {
    if (params.get(deepLinkParams.sortByFinalizedAmount) !== "true") {
        return selectedColumnIds;
    }

    return [...selectedColumnIds, ...contractValueColumnIds.filter((columnId) => !selectedColumnIds.includes(columnId))];
}

export function toContractListSearch(deepLink: ContractListDeepLink) {
    const params = new URLSearchParams();
    if (deepLink.status !== undefined) {
        params.set(deepLinkParams.status, deepLink.status.toString());
    }
    deepLink.excludedStatuses?.forEach((status) => params.append(deepLinkParams.excludedStatuses, status.toString()));
    if (deepLink.endDateWithinYears !== undefined) {
        params.set(deepLinkParams.endDateWithinYears, deepLink.endDateWithinYears.toString());
    }
    if (deepLink.endDateWithinDays !== undefined) {
        params.set(deepLinkParams.endDateWithinDays, deepLink.endDateWithinDays.toString());
    }
    if (deepLink.endDatePassed) {
        params.set(deepLinkParams.endDatePassed, "true");
    }
    if (deepLink.withoutEndDate) {
        params.set(deepLinkParams.withoutEndDate, "true");
    }
    setBoolParam(params, deepLinkParams.withinExpiryNotificationPeriod, deepLink.withinExpiryNotificationPeriod);
    setBoolParam(params, deepLinkParams.autoRenewal, deepLink.autoRenewal);
    setBoolParam(params, deepLinkParams.hasBusinessArea, deepLink.hasBusinessArea);
    setBoolParam(params, deepLinkParams.hasExternalContractParty, deepLink.hasExternalContractParty);
    setBoolParam(params, deepLinkParams.hasFinalizedAmount, deepLink.hasFinalizedAmount);
    if (deepLink.sortByFinalizedAmount) {
        params.set(deepLinkParams.sortByFinalizedAmount, "true");
    }
    if (deepLink.showOnlyMine) {
        params.set(deepLinkParams.showOnlyMine, "true");
    }
    if (deepLink.groupEntityId !== undefined) {
        params.set(deepLinkParams.groupEntityId, deepLink.groupEntityId);
    }
    if (deepLink.currencyCode !== undefined) {
        params.set(deepLinkParams.currencyCode, deepLink.currencyCode);
    }
    if (deepLink.endDateYear !== undefined) {
        params.set(deepLinkParams.endDateYear, deepLink.endDateYear.toString());
    }
    if (deepLink.endDateYearOrLater) {
        params.set(deepLinkParams.endDateYearOrLater, "true");
    }
    deepLink.templateIds?.forEach((templateId) => params.append(deepLinkParams.templateIds, templateId));

    const search = params.toString();
    return search ? `?${search}` : "";
}

// The end date windows are worked out on whichever day the link is opened, so the list holds the contracts the
// dashboard counted that day: a rolling period starting today, or an end date up to yesterday for one that has passed.
export function toContractFilterFromDeepLink(params: URLSearchParams): ContractFilterRequest {
    const today = new Date();
    const filter: ContractFilterRequest = {};
    const status = params.get(deepLinkParams.status);
    if (status !== null) {
        filter.status = { value: [Number(status) as ContractFilterStatus], filterOperator: ListFilterOperator.In };
    }

    const excludedStatuses = params.getAll(deepLinkParams.excludedStatuses);
    if (excludedStatuses.length > 0) {
        filter.status = {
            value: excludedStatuses.map((excludedStatus) => Number(excludedStatus) as ContractFilterStatus),
            filterOperator: ListFilterOperator.NotIn,
        };
    }

    const endDateWithinYears = params.get(deepLinkParams.endDateWithinYears);
    if (endDateWithinYears !== null) {
        filter.endDateRange = endingWithin(Number(endDateWithinYears), RelativeDateUnit.Years);
    }

    const endDateWithinDays = params.get(deepLinkParams.endDateWithinDays);
    if (endDateWithinDays !== null) {
        filter.endDateRange = endingWithin(Number(endDateWithinDays), RelativeDateUnit.Days);
    }

    if (params.get(deepLinkParams.endDatePassed) === "true") {
        filter.endDateRange = { to: toDateOnly(addDays(today, -1)) };
    }

    if (params.get(deepLinkParams.withoutEndDate) === "true") {
        filter.endDateRange = { dateFieldEnum: DateFieldFilterEnum.NoDate };
    }

    assignBoolParam(filter, "withinExpiryNotificationPeriod", params);

    // The renewal date filter only holds auto renewing contracts, and an auto renewing contract's end date is never
    // before today, as the nightly renewal moves it on. Its "no date" option holds every contract that does not renew.
    const autoRenewal = boolParam(params, deepLinkParams.autoRenewal);
    if (autoRenewal !== undefined) {
        filter.renewalDateRange = autoRenewal ? { from: toDateOnly(today) } : { dateFieldEnum: DateFieldFilterEnum.NoDate };
    }

    assignBoolParam(filter, "hasBusinessArea", params);
    assignBoolParam(filter, "hasExternalContractParty", params);
    assignBoolParam(filter, "hasFinalizedAmount", params);

    if (params.get(deepLinkParams.sortByFinalizedAmount) === "true") {
        filter.sortByColumn = ContractColumn.FinalizedAmount;
        filter.sortDirection = SortDirection.DESC;
    }

    if (params.get(deepLinkParams.showOnlyMine) === "true") {
        filter.showOnlyMine = true;
    }

    // The dashboard's group company narrows its counts by the contract's group companies, which the list's group
    // company filter reads the same way.
    const groupEntityId = params.get(deepLinkParams.groupEntityId);
    if (groupEntityId !== null) {
        filter.legalEntityIds = { value: [groupEntityId], filterOperator: ListFilterOperator.In };
    }

    const currencyCode = params.get(deepLinkParams.currencyCode);
    if (currencyCode !== null) {
        filter.currencyCodes = { value: [currencyCode], filterOperator: ListFilterOperator.In };
    }

    const endDateYear = params.get(deepLinkParams.endDateYear);
    if (endDateYear !== null) {
        filter.endDateRange = endingInYear(Number(endDateYear), params.get(deepLinkParams.endDateYearOrLater) === "true");
    }

    const templateIds = params.getAll(deepLinkParams.templateIds);
    if (templateIds.length > 0) {
        filter.templateIds = { value: templateIds, filterOperator: ListFilterOperator.In };
    }

    return filter;
}

function setBoolParam(params: URLSearchParams, name: string, value: boolean | undefined) {
    if (value !== undefined) {
        params.set(name, value.toString());
    }
}

// The deep link and the filter share these names, so the parameter is copied over as it is, left out when not set.
function assignBoolParam(
    filter: ContractFilterRequest,
    name: "withinExpiryNotificationPeriod" | "hasBusinessArea" | "hasExternalContractParty" | "hasFinalizedAmount",
    params: URLSearchParams
) {
    const value = boolParam(params, deepLinkParams[name]);
    if (value !== undefined) {
        filter[name] = value;
    }
}

function endingWithin(value: number, unit: RelativeDateUnit): DateRangeFilter {
    return { rollingPeriod: { value, unit, direction: RelativeDateDirection.After } };
}

// The dashboard also sums contracts that ended earlier this year, so the current year starts on the first of January
// like any other.
function endingInYear(year: number, orLater: boolean): DateRangeFilter {
    const range: DateRangeFilter = { from: toDateOnly(new Date(year, 0, 1)) };
    if (!orLater) {
        range.to = toDateOnly(new Date(year, 11, 31));
    }

    return range;
}

// Sent without a time, so the server reads the user's own calendar day rather than the UTC instant of its midnight.
function toDateOnly(date: Date) {
    return formatISO(date, { representation: "date" });
}
